import { useState } from 'react';
import {
  THEME_TAGS,
  THEME_TAG_CATEGORY_LABELS,
  THEME_TAG_CATEGORY_HINTS,
  type ThemeTagCategory,
} from '@/lib/themeTags';
import {
  BEST_PRACTICES,
  CONVERSION_TABLE,
  MANDATORY_PAGES,
  downloadZifekTagsDoc,
} from '@/lib/themeTagsDoc';

interface ThemeTagsDocModalProps {
  onClose: () => void;
}

const CATEGORY_ORDER: ThemeTagCategory[] = ['simple', 'block', 'marketplace'];

/**
 * Documentation complète des balises Zifek (équivalent du fichier
 * ZIFEK_TEMPLATE_TAGS.md) : consultable dans le Theme Builder et
 * téléchargeable en .md.
 */
export default function ThemeTagsDocModal({ onClose }: ThemeTagsDocModalProps) {
  const [tab, setTab] = useState<'tags' | 'pages' | 'conversion' | 'rules'>('tags');

  const tabs = [
    { key: 'tags', label: 'Balises', icon: 'ri-code-s-slash-line' },
    { key: 'pages', label: 'Pages obligatoires', icon: 'ri-file-list-3-line' },
    { key: 'conversion', label: 'Conversion', icon: 'ri-refresh-line' },
    { key: 'rules', label: 'Bonnes pratiques', icon: 'ri-lightbulb-line' },
  ] as const;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[70]" onClick={onClose} />
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-3 sm:p-4">
        <div className="w-full max-w-4xl h-[88vh] bg-background-50 rounded-lg border border-background-200/70 flex flex-col overflow-hidden shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/70 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-book-2-line text-accent-600"></i>
              <h3 className="text-sm font-semibold text-foreground-950 truncate">
                Documentation des balises Zifek
              </h3>
              <span className="hidden sm:inline text-[10px] font-mono bg-background-100 text-foreground-500 px-1.5 py-0.5 rounded">
                ZIFEK_TEMPLATE_TAGS.md
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={downloadZifekTagsDoc}
                className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-foreground-950 text-background-50 text-[11px] font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
                title="Télécharger le fichier .md"
              >
                <i className="ri-download-2-line"></i>
                Télécharger .md
              </button>
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-background-200/70 bg-background-100/50 flex-shrink-0 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  tab === t.key
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className={t.icon}></i>
                {t.label}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {tab === 'tags' && (
              <div className="space-y-6">
                {CATEGORY_ORDER.map((category) => (
                  <div key={category}>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-700 mb-0.5">
                      {THEME_TAG_CATEGORY_LABELS[category]}
                    </h4>
                    <p className="text-[11px] text-foreground-400 mb-3">{THEME_TAG_CATEGORY_HINTS[category]}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {THEME_TAGS.filter((t) => t.category === category).map((t) => (
                        <div key={t.name} className="border border-background-200/70 rounded-lg p-3 bg-background-50">
                          <code className="text-[11px] font-mono px-2 py-0.5 rounded bg-background-100 text-primary-700 border border-background-200/70">
                            {t.usage}
                          </code>
                          <p className="text-[11px] text-foreground-500 mt-1.5 leading-snug">{t.description}</p>
                          {(t.supportsLimit || t.extraAttrs?.length) && (
                            <p className="text-[10px] text-foreground-400 mt-1">
                              Attributs : {[t.supportsLimit ? 'limit="N"' : '', ...(t.extraAttrs || [])].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'pages' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-accent-200/60 bg-accent-50/60 p-4">
                  <p className="text-xs text-foreground-700 leading-relaxed">
                    Pour qu&apos;un site tienne debout, ces 4 pages de structure <strong>doivent</strong> exister.
                    Sans <code className="font-mono">head</code>, le site n&apos;a ni titre ni SEO ; sans
                    <code className="font-mono"> header</code> ou <code className="font-mono">footer</code>, il est amputé.
                  </p>
                </div>
                <div className="overflow-x-auto rounded-lg border border-background-200/70">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-background-100 text-foreground-600">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Page (page_key)</th>
                        <th className="px-3 py-2 font-semibold">Rôle</th>
                        <th className="px-3 py-2 font-semibold">Balises typiques</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MANDATORY_PAGES.map((p) => (
                        <tr key={p.key} className="border-t border-background-200/70">
                          <td className="px-3 py-2 font-mono text-primary-700">{p.key}</td>
                          <td className="px-3 py-2 text-foreground-600">{p.role}</td>
                          <td className="px-3 py-2 text-foreground-500">{p.tags}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'conversion' && (
              <div className="overflow-x-auto rounded-lg border border-background-200/70">
                <table className="w-full text-left text-xs">
                  <thead className="bg-background-100 text-foreground-600">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Balise d&apos;un autre moteur</th>
                      <th className="px-3 py-2 font-semibold">Balise Zifek à utiliser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CONVERSION_TABLE.map((c, i) => (
                      <tr key={i} className="border-t border-background-200/70">
                        <td className="px-3 py-2 font-mono text-red-600/80">{c.from}</td>
                        <td className="px-3 py-2 font-mono text-accent-700">{c.to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'rules' && (
              <ul className="space-y-2.5">
                {BEST_PRACTICES.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground-600 leading-relaxed">
                    <i className="ri-check-line text-accent-500 mt-0.5"></i>
                    <span>{b}</span>
                  </li>
                ))}
                <li className="mt-4 p-3 rounded-lg bg-red-50 border border-red-100 text-[11px] text-red-700">
                  ❌ Interdit : <code className="font-mono">{'{{merchant.name}}'}</code> ·{' '}
                  <code className="font-mono">{'{{product.price}}'}</code> ·{' '}
                  <code className="font-mono">{'{{cart.item_count}}'}</code> ·{' '}
                  <code className="font-mono">{'{{#products}}…{{/products}}'}</code> ·{' '}
                  <code className="font-mono">{'{% if %}'}</code> ·{' '}
                  <code className="font-mono">{'{{routes.products}}'}</code>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
}